import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft,
  MapPin, 
  AlertCircle,
  Camera,
  ImageIcon,
  CheckCircle,
  Users
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import '../../styles/GalleryCreatePage.css';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * CuratorCreateGallery
 * 
 * Allows curators to create a new gallery/venue.
 * Key differences from admin GalleryCreatePage:
 * - Sets approved=false (requires admin approval)
 * - Can optionally link to an organization
 * - Returns to collection wizard flow after creation
 */
const CuratorCreateGallery = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get org ID and return destination from URL
  const orgId = searchParams.get('org');
  const returnTo = searchParams.get('returnTo'); // 'collection' or null
  
  // Organization state
  const [organization, setOrganization] = useState(null);
  
  // Gallery data state
  const [galleryData, setGalleryData] = useState({
    name: '',
    type: '',
    description: '',
    address: '',
    website: '',
    contact_email: '',
    contact_phone: ''
  });
  
  // Location data state
  const [locationData, setLocationData] = useState({
    formatted_address: '',
    latitude: null,
    longitude: null,
    geo_point: null
  });
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Image states
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Form states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  const coverImageRef = React.useRef(null);
  const logoRef = React.useRef(null);

  // Gallery types
  const galleryTypes = [
    { value: 'gallery', label: 'Gallery' },
    { value: 'public space', label: 'Public Space' },
    { value: 'fair', label: 'Fair' },
    { value: 'studio', label: 'Studio' },
    { value: 'restaurant/bar', label: 'Restaurant/Bar' },
    { value: 'Private Studio', label: 'Private Studio' },
    { value: 'event venue', label: 'Event Venue' },
    { value: 'online', label: 'Online' },
    { value: 'Museum', label: 'Museum' },
    { value: 'Pop Up', label: 'Pop Up' }
  ];

  // Load organization info if org ID provided
  useEffect(() => {
    if (orgId) {
      loadOrganization();
    }
  }, [orgId]);

  const loadOrganization = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('organization_id, name, slug, logo')
        .eq('organization_id', parseInt(orgId))
        .single();
      
      if (!error && data) {
        setOrganization(data);
      }
    } catch (err) {
      console.error('Error loading organization:', err);
    }
  };

  // Handle keyboard navigation for address suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || addressSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < addressSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : addressSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < addressSuggestions.length) {
          selectSuggestion(addressSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const selectSuggestion = (suggestion) => {
    setGalleryData(prev => ({
      ...prev,
      address: suggestion.formatted_address
    }));
    setLocationData({
      formatted_address: suggestion.formatted_address,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
      geo_point: suggestion.geo_point
    });
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    setGeocodingError(null);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setGalleryData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (field === 'address') {
      setGeocodingError(null);
      
      if (!value.trim()) {
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setLocationData({
          formatted_address: '',
          latitude: null,
          longitude: null,
          geo_point: null
        });
        return;
      }
      
      debouncedGeocode(value);
    }
  };

  // Geocoding function
  const geocodeAddress = async (address) => {
    if (!address || address.trim().length < 3) {
      setLocationData({
        formatted_address: '',
        latitude: null,
        longitude: null,
        geo_point: null
      });
      return;
    }

    setGeocodingLoading(true);
    setGeocodingError(null);
    
    try {
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;
      
      const response = await fetch('https://api.vibrantcast.com/functions/v1/google_maps_location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          input: address,
          user_lat: 41.8781,
          user_lng: -87.6298,
          radius: 50
        })
      });

      if (!response.ok) {
        throw new Error('Failed to geocode address');
      }

      const results = await response.json();
      
      if (results && results.length > 0) {
        setAddressSuggestions(results.slice(0, 5));
        setShowSuggestions(true);
        setSelectedSuggestionIndex(-1);
        setGeocodingError(null);
      } else {
        setGeocodingError('No location found for this address');
        setShowSuggestions(false);
        setAddressSuggestions([]);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setGeocodingError('Failed to geocode address. Please try again.');
    } finally {
      setGeocodingLoading(false);
    }
  };

  const debouncedGeocode = useCallback(
    debounce((address) => {
      geocodeAddress(address);
    }, 300),
    []
  );

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    try {
      if (type === 'cover') {
        setUploadingCover(true);
        const result = await uploadToCloudinary(file, 'image', 'galleries/covers');
        setCoverImageUrl(result.url);
      } else if (type === 'logo') {
        setUploadingLogo(true);
        const result = await uploadToCloudinary(file, 'image', 'galleries/logos');
        setLogoUrl(result.url);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(`Failed to upload ${type} image: ${error.message}`);
    } finally {
      if (type === 'cover') setUploadingCover(false);
      if (type === 'logo') setUploadingLogo(false);
    }
  };

  const removeImage = (type) => {
    if (type === 'cover') {
      setCoverImageUrl('');
    } else if (type === 'logo') {
      setLogoUrl('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!galleryData.name) {
      setError('Gallery name is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error: insertError } = await supabase
        .from('galleries')
        .insert([{
          name: galleryData.name,
          type: galleryData.type || null,
          description: galleryData.description || null,
          address: galleryData.address || null,
          website: galleryData.website || null,
          contact_email: galleryData.contact_email || null,
          contact_phone: galleryData.contact_phone || null,
          cover_image: coverImageUrl || null,
          logo: logoUrl || null,
          approved: false, // CURATOR: Requires admin approval
          added_by: user.id,
          organization_id: orgId ? parseInt(orgId) : null,
          point_location: locationData.latitude && locationData.longitude 
            ? `${locationData.latitude},${locationData.longitude}` 
            : null,
          geo_point: locationData.geo_point || null
        }])
        .select('*')
        .single();

      if (insertError) throw insertError;

      // Also grant the creator gallery access
      await supabase
        .from('gallery_access')
        .insert({
          user_uuid: user.id,
          gallery_id: data.gallery_id,
          access_level: 'admin',
          is_active: true
        });

      setSuccess('Gallery submitted for approval! An admin will review it shortly.');
      
      // Navigate based on return destination
      setTimeout(() => {
        if (returnTo === 'collection' && data.gallery_id) {
          // Go back to collection wizard with gallery pre-selected
          navigate(`/curator/collections/new?org=${orgId}&gallery=${data.gallery_id}`);
        } else {
          navigate('/curator/galleries');
        }
      }, 2000);

    } catch (error) {
      console.error('Error creating gallery:', error);
      setError(`Failed to create gallery: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (returnTo === 'collection' && orgId) {
      navigate(`/curator/collections/new?org=${orgId}`);
    } else {
      navigate('/curator/galleries');
    }
  };

  // Helper for Cloudinary transforms
  const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', `/upload/${transform}`);
  };

  return (
    <div className="gallery-create-page">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <button onClick={handleCancel} className="back-button" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          background: 'none',
          border: 'none',
          color: '#6c757d',
          cursor: 'pointer',
          padding: 0,
          marginBottom: '1rem'
        }}>
          <ArrowLeft size={16} />
          Back
        </button>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Add New Gallery/Venue</h1>
        
        {/* Pending approval notice */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          background: '#fff3cd',
          borderRadius: '8px',
          marginTop: '1rem',
          color: '#856404'
        }}>
          <AlertCircle size={18} />
          <span style={{ fontSize: '0.875rem' }}>
            New galleries require admin approval before they can be used.
          </span>
        </div>

        {/* Organization indicator */}
        {organization && (
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            background: '#f0f9ff',
            borderRadius: '8px',
            marginTop: '0.75rem'
          }}>
            {organization.logo ? (
              <img 
                src={addCloudinaryTransform(organization.logo)}
                alt={organization.name}
                style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
              />
            ) : (
              <Users size={20} color="#059669" />
            )}
            <div>
              <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                Creating for: {organization.name}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Success Message */}
      {success && (
        <div className="success-message" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '1rem',
          background: '#d4edda',
          borderRadius: '8px',
          color: '#155724',
          marginBottom: '1rem'
        }}>
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '1rem',
          background: '#f8d7da',
          borderRadius: '8px',
          color: '#721c24',
          marginBottom: '1rem'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="gallery-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="name">Gallery/Venue Name *</label>
            <input
              type="text"
              id="name"
              value={galleryData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter gallery or venue name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              value={galleryData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Type</option>
              {galleryTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={galleryData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              placeholder="Describe the gallery/venue..."
              disabled={loading}
            />
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <h3>Location</h3>
          
          <div className="form-group" style={{ position: 'relative' }}>
            <label htmlFor="address">
              <MapPin size={14} style={{ marginRight: '0.25rem' }} />
              Address
            </label>
            <input
              type="text"
              id="address"
              value={galleryData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Start typing an address..."
              disabled={loading}
              autoComplete="off"
            />
            {geocodingLoading && (
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                Searching...
              </div>
            )}
            {geocodingError && (
              <div style={{ fontSize: '0.75rem', color: '#dc3545', marginTop: '0.25rem' }}>
                {geocodingError}
              </div>
            )}
            
            {/* Address suggestions dropdown */}
            {showSuggestions && addressSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {addressSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => selectSuggestion(suggestion)}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      borderBottom: index < addressSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                      background: index === selectedSuggestionIndex ? '#f8f9fa' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                  >
                    <MapPin size={14} color="#6c757d" />
                    <span style={{ fontSize: '0.875rem' }}>{suggestion.formatted_address}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <h3>Contact Information (Optional)</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                id="website"
                value={galleryData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://..."
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="contact_email">Email</label>
              <input
                type="email"
                id="contact_email"
                value={galleryData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                placeholder="gallery@example.com"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact_phone">Phone</label>
            <input
              type="tel"
              id="contact_phone"
              value={galleryData.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              placeholder="(555) 123-4567"
              disabled={loading}
            />
          </div>
        </div>

        {/* Images */}
        <div className="form-section">
          <h3>Images (Optional)</h3>
          
          <div className="form-row">
            {/* Cover Image */}
            <div className="form-group">
              <label>Cover Image</label>
              <div className="image-upload-container">
                {coverImageUrl ? (
                  <div className="image-preview" style={{ position: 'relative' }}>
                    <img 
                      src={coverImageUrl} 
                      alt="Cover preview" 
                      style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage('cover')}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => coverImageRef.current?.click()}
                    style={{
                      border: '2px dashed #dee2e6',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      color: '#6c757d'
                    }}
                  >
                    {uploadingCover ? (
                      <span>Uploading...</span>
                    ) : (
                      <>
                        <Camera size={24} style={{ marginBottom: '0.5rem' }} />
                        <div style={{ fontSize: '0.875rem' }}>Click to upload cover</div>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={coverImageRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleImageUpload(e.target.files[0], 'cover')}
                />
              </div>
            </div>

            {/* Logo */}
            <div className="form-group">
              <label>Logo</label>
              <div className="image-upload-container">
                {logoUrl ? (
                  <div className="image-preview" style={{ position: 'relative' }}>
                    <img 
                      src={logoUrl} 
                      alt="Logo preview" 
                      style={{ width: '100%', height: '120px', objectFit: 'contain', borderRadius: '8px', background: '#f8f9fa' }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage('logo')}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => logoRef.current?.click()}
                    style={{
                      border: '2px dashed #dee2e6',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      color: '#6c757d'
                    }}
                  >
                    {uploadingLogo ? (
                      <span>Uploading...</span>
                    ) : (
                      <>
                        <ImageIcon size={24} style={{ marginBottom: '0.5rem' }} />
                        <div style={{ fontSize: '0.875rem' }}>Click to upload logo</div>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={logoRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions" style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #e9ecef'
        }}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9375rem'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !galleryData.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: loading || !galleryData.name ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading || !galleryData.name ? 'not-allowed' : 'pointer',
              fontSize: '0.9375rem',
              fontWeight: 500
            }}
          >
            <Save size={18} />
            {loading ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CuratorCreateGallery;
